import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getIn } from 'formik';

import Select from '../../select/select.component';
import AppkitIcon from '../../appkitIcon/appkitIcon.component';
import Spinner from '../../spinner.component';
import ErrorMessage from './errorMessage.component';
import { FormikPropTypes, SelectValuePropTypes, SelectOptionPropTypes } from '../propTypes';

const SDKSelectFormik = ({
  label,
  name,
  onSelect,
  formik,
  value,
  options,
  clearable = false,
  virtualized = false,
  hidden = false,
  clearWhenOptionsChanged = false,
  className = '',
  handleExternalChange = false,
  loading = false,
  disabled = false,
  ...rest
}) => {
  useEffect(() => {
    if (clearWhenOptionsChanged) {
      const foundOption = options.find(option => option.value === value);
      if (value && !foundOption) {
        const initialValue = getIn(formik.initialValues, name);
        formik.setFieldValue(name, initialValue);
      }
    }
  }, [options, clearWhenOptionsChanged]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (handleExternalChange) {
      formik.setFieldValue(name, value);
    }
  }, [name, value, handleExternalChange]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = useCallback(option => {
    if (!option) {
      return;
    }

    const { value, label } = option;
    formik.setTouched({
      ...formik.touched,
      [name]: true,
    });
    if (onSelect) {
      onSelect(value, { formik, label });
    }
    formik.setFieldValue(name, value);
  }, [name, formik, onSelect]);

  const clearSelection = useCallback(() => {
    formik.setFieldValue(name, null);
  }, [name, formik]);

  if (hidden) {
    return null;
  }

  const showClearIcon = clearable && Boolean(value);

  const error = getIn(formik.errors, name);
  const touched = getIn(formik.touched, name);

  const shouldUseErrorStyles = touched && error;

  const classNames = classnames([
    'sdk-custom-select',
    {
      'with-error': shouldUseErrorStyles,
    },
  ]);

  return (
    <div className={`sdk-select-formik-group ${className}`}>
      {label ? <label className="a-form-label">{label} {(loading && <Spinner small />)}</label> : null}
      <div className="sdk-custom-select-wrapper sdk-small-custom-context-wrapper" data-testid={name}>
        <Select
          className={classNames}
          onChange={handleSelect}
          virtualized={virtualized}
          name={name}
          value={value}
          options={options}
          disabled={disabled || loading}
          {...rest}
        />
        <AppkitIcon
          className="sdk-custom-select-appkit-icon"
          icon="down-chevron"
          type="fill"
          size={12}
        />
        {
          showClearIcon && (
            <AppkitIcon
              className="clear-icon"
              icon="close"
              type="fill"
              size={8}
              onClick={clearSelection}
            />
          )
        }
      </div>
      <div className="invalid-feedback">
        <ErrorMessage name={name} formik={formik} />
      </div>
    </div>
  );
};

SDKSelectFormik.propTypes = {
  label: PropTypes.node,
  name: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
  formik: FormikPropTypes.isRequired,
  clearable: PropTypes.bool,
  virtualized: PropTypes.bool,
  value: SelectValuePropTypes,
  hidden: PropTypes.bool,
  options: PropTypes.arrayOf(SelectOptionPropTypes),
  clearWhenOptionsChanged: PropTypes.bool,
  className: PropTypes.string,
  handleExternalChange: PropTypes.bool,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default SDKSelectFormik;
